/**
 * Typed wrappers for service-customer (port 3040 by default) and the
 * service-identity registration endpoint (port 3013).
 *
 * Citizen self-registration goes through service-identity at
 * `POST /v1/auth/register`; service-identity creates the user_account, hardcodes
 * the customer role grant, calls service-customer's m2m
 * `POST /v1/customers/internal` to materialise the Customer profile, and
 * returns access + refresh tokens. The form lands the user authenticated.
 */
import {
  ApiError,
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

/** Subset of `RegisterSchema` from
 *  `apps/service-identity/src/modules/auth/dto/register.dto.ts`. */
export interface CustomerRegistrationRequest {
  readonly fullName: string;
  readonly primaryPhone: string;
  /** service-identity requires email — the registration endpoint uses it as
   *  the unique login key. */
  readonly primaryEmail: string;
  readonly password: string;
  readonly displayName?: string;
}

/** Customer create response — narrowed to the fields the UI actually uses. */
export interface CustomerResponse {
  readonly id: string;
  readonly customerNumber: string;
  readonly fullName: string;
  readonly primaryPhone: string;
  readonly primaryEmail?: string;
  readonly grade: 'WALK_IN' | 'REGISTERED';
  readonly kycLevel: 'NONE' | 'BASIC' | 'ENHANCED' | 'INSTITUTION';
  readonly displayName?: string;
  readonly preferences?: Record<string, unknown>;
  readonly deactivatedAt?: string | null;
}

/** Shape returned by `POST /v1/auth/register` — `IssuedTokens` plus the
 *  inline `customer` summary. */
export interface RegistrationResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
  readonly tokenType: 'Bearer';
  readonly customer: {
    readonly id: string;
    readonly customerNumber: string;
    readonly kycLevel: string;
    readonly grade: string;
  };
}

/** Subset of `UpdateCustomerSchema` from service-customer. */
export interface UpdateCustomerRequest {
  readonly fullName?: string;
  readonly displayName?: string;
  readonly primaryEmail?: string | null;
  readonly preferences?: Record<string, unknown>;
}

/** Phase-1b stub: no backend endpoint exists for password reset — service-identity
 *  owns auth (see infrastructure/start/local.md §6). The form POSTs here so a
 *  future endpoint can slot in without a UI change; for now any 404 is
 *  swallowed so the success state still renders in dev. */
export interface PasswordResetRequest {
  readonly identifier: string;
}

export async function registerCustomer(
  body: CustomerRegistrationRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<RegistrationResult> {
  return requestJson<RegistrationResult>({
    method: 'POST',
    baseUrl: env.identityBaseUrl,
    path: '/v1/auth/register',
    body: {
      email: body.primaryEmail,
      password: body.password,
      fullName: body.fullName,
      primaryPhone: body.primaryPhone,
      ...(body.displayName ? { displayName: body.displayName } : {}),
    },
    ...options,
  });
}

/**
 * Fetch a customer by id. The portal calls this with the `customerId` claim
 * baked into the JWT (see `lib/auth/jwt.ts`).
 *
 * Server-side this is `GET /v1/customers/:id`, RBAC-gated by
 * `@RequirePermission('Customer','read')`. In dev with a service-identity-deferred
 * token the call may 401/403 — the caller surfaces that as an inline notice.
 */
export async function getCustomerById(
  id: string,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & { bearerToken?: string } = {},
): Promise<CustomerResponse> {
  const { bearerToken, ...rest } = options;
  return requestJson<CustomerResponse>({
    method: 'GET',
    baseUrl: env.serviceCustomerBaseUrl,
    path: `/v1/customers/${encodeURIComponent(id)}`,
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}

/**
 * Patch-style profile update. Service-customer exposes `PUT /v1/customers/:id`
 * with `UpdateCustomerSchema` (all fields optional), so a partial body is the
 * normal call shape.
 */
export async function updateCustomer(
  id: string,
  body: UpdateCustomerRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & { bearerToken?: string } = {},
): Promise<CustomerResponse> {
  const { bearerToken, ...rest } = options;
  return requestJson<CustomerResponse>({
    method: 'PUT',
    baseUrl: env.serviceCustomerBaseUrl,
    path: `/v1/customers/${encodeURIComponent(id)}`,
    body,
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}

export async function requestPasswordReset(
  body: PasswordResetRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<void> {
  try {
    await requestJson<unknown>({
      method: 'POST',
      baseUrl: env.serviceCustomerBaseUrl,
      path: '/v1/customers/password-reset-request',
      body,
      ...options,
    });
  } catch (err) {
    // Endpoint is intentionally absent today (service-identity-owned). Treat 404 /
    // 405 as a "received-and-acknowledged" UX so the screen progresses to
    // the success step. Surface real failures to the caller.
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      return;
    }
    throw err;
  }
}
